const { TAB_NAVIGATION_ACTIVATED_EVENT } = require('../../../util/tab_navigation_events.js');

var SPECTRUM_LEFT_MARGIN_WIDTH = 24;
var MAX_LOAD_RETRIES = 7;

/**
 * Controller for the tab-navigation directive.
 * @param {Object} $scope - AngularJS scope service.
 * @param {Function} $timeout - AngularJS timeout service.
 * @param {Window} $window - AngularJS window service wrapper.
 * @ngInject
 */
function TabNavigationController($scope, $timeout, $window) {
  var self = this;
  self.$scope = $scope;
  self.$timeout = $timeout;
  self.$window = $window;
  self.indicatorWidth = this.defaultTabWidth || 0;
  self.indicatorLeft = 0;
  self._loadAttempts = 0;
  self._inkRightCache = {};
  self._newBadgeUpdatesRegistered = false;
  self._loadDefaultTab(this.currentTab);

  self.$scope.$watch(function(){
    return self.currentTab;
  }, function(){
    self._updateTabStyling();
  });
}

function getCanvasContext(win) {
  var doc = win && win.document;
  if (!doc || !doc.createElement) {
    return null;
  }
  var canvas = doc.createElement('canvas');
  return canvas && canvas.getContext ? canvas.getContext('2d') : null;
}

/**
 * Measures the subpixel distance from the text origin to its furthest painted ink pixel.
 * Design requires centering the indicator dot on the rightmost ink edge across arbitrary
 * internationalized text, excluding whitespace font side bearings.
 * @param {HTMLElement} btn - The tab button element providing computed font styles.
 * @param {string} label - The text label to measure.
 * @param {Window} win - The window service.
 * @returns {number|null} The subpixel right ink bound, or null if unmeasurable.
 */
function measureInkRight(btn, label, win) {
  try {
    var ctx = getCanvasContext(win);
    if (!ctx || !ctx.measureText) {
      return null;
    }
    var getComputed = win && win.getComputedStyle;
    var computed = getComputed ? getComputed(btn) : {};
    ctx.font = (computed.fontWeight || '400') + ' ' + (computed.fontSize || '14px') + ' ' + (computed.fontFamily || 'adobe-clean, sans-serif');
    var metrics = ctx.measureText(label);
    if (metrics && typeof metrics.actualBoundingBoxRight === 'number') {
      return metrics.actualBoundingBoxRight;
    }
  } catch (error) {
    return null;
  }
  return null;
}

TabNavigationController.prototype._registerNewBadgeUpdates = function() {
  if (this._newBadgeUpdatesRegistered) {
    return;
  }
  this._newBadgeUpdatesRegistered = true;

  var self = this;
  var destroyed = false;
  var resizePromise = null;
  var doc = self.$window.document;
  if (doc && doc.fonts && doc.fonts.ready) {
    doc.fonts.ready.then(function() {
      if (destroyed) {
        return;
      }
      self._inkRightCache = {};
      self._updateTabStyling();
      self.$scope.$evalAsync();
    });
  }

  var onResize = function() {
    self._inkRightCache = {};
    if (resizePromise) {
      self.$timeout.cancel(resizePromise);
    }
    resizePromise = self.$timeout(function() {
      resizePromise = null;
      self._updateTabStyling();
    }, 100);
  };
  if (self.$window.addEventListener) {
    self.$window.addEventListener('resize', onResize);
  }
  self.$scope.$on('$destroy', function() {
    destroyed = true;
    if (self.$window.removeEventListener) {
      self.$window.removeEventListener('resize', onResize);
    }
    if (resizePromise) {
      self.$timeout.cancel(resizePromise);
    }
  });
};

/**
 * Returns dynamic style positioning the new dot over the label's right ink edge.
 * @param {Object} tab - The tab descriptor object.
 * @returns {Object} Style object containing the calculated left offset.
 */
TabNavigationController.prototype.newDotStyle = function(tab) {
  if (!tab || !tab.newBadge) {
    return {};
  }
  this._registerNewBadgeUpdates();
  var tabId = tab.id;
  this._inkRightCache = this._inkRightCache || {};

  var doc = this.$window && this.$window.document;
  var btn = doc && doc.getElementById ? doc.getElementById(tabId) : null;
  if (!btn) {
    var cachedInk = this._inkRightCache[tabId];
    if (typeof cachedInk === 'number') {
      return { left: cachedInk + 'px' };
    }
    return {};
  }

  var inkRight = this._inkRightCache[tabId];
  if (inkRight === undefined) {
    inkRight = measureInkRight(btn, tab.label, this.$window);
    this._inkRightCache[tabId] = inkRight;
  }

  return typeof inkRight === 'number' ? { left: inkRight + 'px' } : {};
};

/**
 * Returns the style CSS for the indicator, allowing it to be dynamic based on controller variables.
 */

TabNavigationController.prototype.indicatorStyle = function() {
  return { width: this.indicatorWidth + 'px', left: this.indicatorLeft + 'px', bottom: '0' };
};


/**
 * Checks if the tab passed in is the currently selected tab.
 */

TabNavigationController.prototype.isTabSelected = function(tab) {
  return this.currentTab === tab;
};

/**
 * Selects the passed in tab.
 */

TabNavigationController.prototype.selectTab = function(newTab) {
  var self = this;
  self.currentTab = newTab;
  self._updateTabStyling();
  /* A timeout is needed to ensure that the parent controller callback is invoked after the digest cycle completes */
  self.$timeout(function(){
    self.onTabChange();
  }, 0, false);
};

/**
 * Handles the click on a tab to make it current.
 */

TabNavigationController.prototype.handleTabChange = function(newTab) {
  try {
    this.$window.dispatchEvent(new CustomEvent(TAB_NAVIGATION_ACTIVATED_EVENT, { detail: newTab.id }));
  } catch (error) {
    // Event dispatch failures must not interrupt tab navigation.
  }
  if (newTab.link) {
    this.$window.history.replaceState(null, null, newTab.link);
    this.$window.location.reload();
  } else {
    this.$window.history.replaceState({ navigationTabClicked: true }, null, '#' + newTab.id + '-section');
  }
  this.selectTab(newTab);
};

/**
 * Gets the width of the tab element.
 */

TabNavigationController.prototype.getWidthOfTab = function(tab) {
  var width = 0;
  try {
    width = document.getElementById(tab.id).clientWidth;
  } catch (error) {
    return width;
  }
  return width;
};

TabNavigationController.prototype._isTabLoaded = function(tab){
  return this.getWidthOfTab(tab) > 0;
};

TabNavigationController.prototype._loadDefaultTab = function(tab) {
  // In order to get the correct left offset and width for the selected tab indicator
  // the tab has to be loaded by Angular in the DOM. Angular does not provide a reliable way to know when that occurs.
  // Sticking  function in a 0 second timeout schedules the execution for the end of the queue,
  // which if done a few times, will place it after the DOM loads.
  // This checks if the content has loaded yet, and if not, schedules a retry if the max # of retry
  // attempts hasn't been exceeded.
  var self = this;
  if (!self._isTabLoaded(tab) && self._loadAttempts < MAX_LOAD_RETRIES) {
    self._loadAttempts++;

    return self.$timeout(function() {
      self._loadDefaultTab();
    });
  } else {
    self.selectTab(this.currentTab);
    self._updateTabStyling();
  }
};

/**
 * Moves indicator over by the size of all previous tabs + margins between them.
 * Changes indicator size to be same size as new tab.
 */

TabNavigationController.prototype._updateTabStyling = function() {
  var self = this;
  self.indicatorWidth = self.getWidthOfTab(self.currentTab);

  var left = 0;
  for (var oldTab in this.navigationTabs) {
    if (self.navigationTabs[oldTab] === self.currentTab) {
      break;
    }
    left += SPECTRUM_LEFT_MARGIN_WIDTH;
    left += self.getWidthOfTab(this.navigationTabs[oldTab]);
  }

  this.indicatorLeft = left;
};

module.exports = TabNavigationController;
